import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Sparkle, Check, ArrowRight, Star, Crown, EnvelopeSimple } from '@phosphor-icons/react';

// Plan comparison shown when the user clicks the "Upgrade" pill anywhere in
// the app. Three side-by-side cards: Free · Business · Premium.
//
// Display rules (per product requirements):
//  - Free card only lists features that ARE included. No strike-through of
//    locked features — it looks cluttered.
//  - Business card starts with an "Everything in Free, plus" line and then
//    only lists features NEW in Business.
//  - Premium card starts with "Everything in Business, plus" and lists
//    Premium-only add-ons. Premium is sales-led: minimum 20 users, priced
//    at ₹699/user/mo, bought via support@inspiroworks.com.
//
// The Business price + storage numbers here MUST stay in sync with
// `backend/paymentsService.js` (PRICE_PER_USER) and
// `backend/lib/storageQuota.js` (PLAN_DEFAULT_MB). Premium is currently
// sales-only so there's no runtime enforcement to mirror.

const PREMIUM_CONTACT_EMAIL = 'support@inspiroworks.com';
const PREMIUM_MAILTO = `mailto:${PREMIUM_CONTACT_EMAIL}?subject=${encodeURIComponent('Inspiro Projects — Premium plan inquiry')}&body=${encodeURIComponent('Hi Inspiro Projects team,\n\nWe\'d like to upgrade our workspace to the Premium plan.\n\nTeam size: \nWorkspace name: \nPreferred start date: \n\nThanks!')}`;

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: '\u20B90',
    priceSub: 'forever',
    tagline: 'For small teams trying out the basics',
    // Listed features are the ones that ARE included on Free.
    features: [
      'Up to 2 members',
      'Unlimited projects & tasks',
      'All 4 templates (Scrum, Kanban, Bug Tracker, Blank)',
      'Kanban, List, Backlog views + sprints & story points',
      'Rich-text editor, comments, @mentions, attachments',
      'Linked issues & dependency-aware Done guard',
      'Activity history per task',
      '5 MB max per file upload  ·  100 MB total storage',
    ],
  },
  {
    id: 'business',
    name: 'Business',
    price: '\u20B9399',
    priceSub: 'per user / month',
    yearlyNote: 'or \u20B9359/user/mo billed yearly · save 10%',
    tagline: 'Built for growing teams that need real collaboration',
    badge: 'Most popular',
    inheritsFrom: 'Free',
    // Only what's NEW compared to Free.
    features: [
      'Unlimited members',
      '10 MB max per file upload  ·  10 GB total storage',
      'Project Health, Velocity, Burndown charts',
      'Slack & Microsoft Teams integrations',
      'CSV import / export · Bulk operations',
      'Docs / Pages (15 per project) & Whiteboards (5 per project)',
      'In-product support tickets · Priority email',
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    price: '\u20B9699',
    priceSub: 'per user / month',
    yearlyNote: 'Minimum 20 users · annual contract',
    tagline: 'For enterprises that need dedicated infra, branding and SLA',
    salesLed: true,
    inheritsFrom: 'Business',
    features: [
      'Up to 200 GB total storage',
      'Dedicated storage configurations',
      'Region-based data storage',
      'Custom domain',
      'Custom branding & white-label',
      'Custom email notifications',
      'Priority support with SLA',
      'Advanced analytics & insights',
      'SSO / SAML integration',
      'API access',
    ],
  },
];

function planByTier(tier) {
  return PLANS.find(p => p.id === tier) || PLANS[0];
}

export default function UpgradeDialog({ open, onClose }) {
  const { status } = useSubscription();
  const navigate = useNavigate();
  // Normalize the user's current plan. We treat any `active` / `trial`
  // subscription as `business` for now; `premium` will be surfaced once
  // the backend understands the premium tier.
  const currentTier = (() => {
    const t = status?.plan_tier || status?.plan;
    if (t === 'premium') return 'premium';
    if (t === 'business' || status?.plan === 'active' || status?.plan === 'trial') return 'business';
    return 'free';
  })();
  const current = planByTier(currentTier);

  const usage = useMemo(() => {
    if (!status) return null;
    const used = status.used_seats ?? 0;
    const limit = currentTier === 'free' ? (status.free_plan_limit ?? 2) : (status.billable_users ?? '∞');
    return { used, limit };
  }, [status, currentTier]);

  const goToBilling = () => { onClose?.(); navigate('/billing'); };

  const headerTitle = (() => {
    if (currentTier === 'free') return 'Get more out of Inspiro Projects with Business';
    if (currentTier === 'business') return 'Scale further with Premium';
    return `You\u2019re on the ${current.name} plan \u2014 here\u2019s everything you have access to.`;
  })();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-5xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto" data-testid="upgrade-dialog">
        {/* Header — bold gradient strip so the upgrade prompt feels like an
            event, not a settings page. */}
        <div className="bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 px-7 py-6 text-white">
          <div className="flex items-center gap-2 mb-2">
            <Sparkle size={18} weight="fill" />
            <p className="text-[10.5px] uppercase tracking-[0.18em] font-bold opacity-90">
              {currentTier === 'premium' ? 'Your workspace plan' : 'Upgrade your workspace'}
            </p>
          </div>
          <DialogTitle className="font-['Outfit'] text-[24px] font-extrabold tracking-tight leading-tight">
            {headerTitle}
          </DialogTitle>
          {usage && (
            <p className="text-[12.5px] mt-2 opacity-90">
              Currently on <strong className="font-bold">{current.name}</strong> · {usage.used} of {usage.limit} member seats used.
            </p>
          )}
        </div>

        <div className="px-7 py-6 bg-zinc-50/50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PLANS.map(plan => (
              <PlanCard
                key={plan.id}
                plan={plan}
                isCurrent={plan.id === currentTier}
                onUpgrade={plan.id === 'business' ? goToBilling : undefined}
              />
            ))}
          </div>

          <div className="mt-5 flex items-center justify-between gap-3 text-[11.5px] text-zinc-500 flex-wrap">
            <p>Cancel anytime · 14-day money-back guarantee · No setup fees.</p>
            <button onClick={onClose} className="text-zinc-500 hover:text-zinc-800 underline underline-offset-2" data-testid="upgrade-dialog-close">Maybe later</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PlanCard({ plan, isCurrent, onUpgrade }) {
  const isFree = plan.id === 'free';
  const isBusiness = plan.id === 'business';
  const isPremium = plan.id === 'premium';

  // Visual tiers: Premium = dark luxe card, Business = clean white card with
  // indigo ring, Free = muted zinc card.
  const cardCls = isPremium
    ? 'bg-gradient-to-br from-zinc-900 via-slate-900 to-indigo-950 text-white ring-1 ring-white/10 shadow-[0_10px_32px_rgba(30,27,75,0.35)]'
    : isBusiness
      ? 'bg-white ring-1 ring-indigo-200 shadow-[0_8px_28px_rgba(99,102,241,0.16)]'
      : 'bg-zinc-50 ring-1 ring-zinc-200';

  const mutedText = isPremium ? 'text-indigo-200' : 'text-zinc-500';
  const bodyText = isPremium ? 'text-zinc-100' : 'text-zinc-800';
  const headText = isPremium ? 'text-white' : 'text-zinc-900';
  const inheritBadge = isPremium
    ? 'bg-white/10 text-indigo-100 ring-white/15'
    : 'bg-indigo-50 text-indigo-700 ring-indigo-100';

  return (
    <div className={`relative rounded-2xl p-5 flex flex-col ${cardCls}`} data-testid={`plan-card-${plan.id}`}>
      {/* Top-right status ribbon */}
      {plan.badge && !isCurrent && isBusiness && (
        <span className="absolute -top-2.5 right-5 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-wider">
          <Star size={10} weight="fill" />{plan.badge}
        </span>
      )}
      {isCurrent && (
        <span className="absolute top-3 right-3 inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wider" data-testid={`plan-card-${plan.id}-current`}>
          <Check size={10} weight="bold" className="mr-0.5" />Your plan
        </span>
      )}

      {/* Header */}
      <div className="flex items-center gap-2">
        {isPremium && (
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-md">
            <Crown size={14} weight="fill" className="text-white" />
          </div>
        )}
        <p className={`text-[10.5px] uppercase tracking-[0.16em] font-bold ${mutedText}`}>{plan.name} plan</p>
      </div>

      <div className="mt-1 flex items-baseline gap-1.5">
        <span className={`font-['Outfit'] text-[28px] font-extrabold tracking-tight ${headText}`}>{plan.price}</span>
        <span className={`text-[12px] font-medium ${mutedText}`}>{plan.priceSub}</span>
      </div>
      {plan.yearlyNote && <p className={`mt-1 text-[10.5px] font-medium ${mutedText}`}>{plan.yearlyNote}</p>}
      <p className={`text-[12.5px] mt-1.5 leading-snug ${isPremium ? 'text-indigo-100' : 'text-zinc-600'}`}>{plan.tagline}</p>

      {/* "Everything in <prev>, plus" pill */}
      {plan.inheritsFrom && (
        <div className={`mt-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ring-1 ${inheritBadge} text-[11px] font-semibold self-start`}>
          <Check size={11} weight="bold" />
          Everything in {plan.inheritsFrom}, plus
        </div>
      )}

      {/* Feature list — only positives, no strike-through */}
      <ul className="mt-3 space-y-1.5 flex-1">
        {plan.features.map(f => (
          <li key={f} className="flex items-start gap-2 text-[12.5px]">
            <Check size={14} weight="bold" className={`mt-[2px] flex-shrink-0 ${isPremium ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <span className={bodyText}>{f}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      {isFree && (
        isCurrent
          ? <p className={`mt-5 text-[11px] italic ${mutedText}`}>You\u2019re on this plan.</p>
          : <p className={`mt-5 text-[11px] italic ${mutedText}`}>Listed for comparison.</p>
      )}
      {isBusiness && !isCurrent && (
        <Button
          onClick={onUpgrade}
          data-testid="plan-card-business-cta"
          className="mt-5 w-full h-10 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white text-[13px] font-bold tracking-tight shadow-md"
        >
          Upgrade to Business
          <ArrowRight size={14} weight="bold" className="ml-2" />
        </Button>
      )}
      {isPremium && !isCurrent && (
        <a
          href={PREMIUM_MAILTO}
          data-testid="plan-card-premium-cta"
          className="mt-5 w-full h-10 inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white text-[13px] font-bold tracking-tight shadow-md px-4"
        >
          <EnvelopeSimple size={14} weight="bold" />
          Contact Support to Upgrade
        </a>
      )}
      {isPremium && !isCurrent && (
        <p className="mt-2 text-[10.5px] text-indigo-200 text-center">
          or email <a href={PREMIUM_MAILTO} className="underline underline-offset-2 text-amber-300">{PREMIUM_CONTACT_EMAIL}</a>
        </p>
      )}
    </div>
  );
}
